import {useState} from 'react'
import {Label, Text, Box, CounterLabel, Link} from '@primer/react'
import {EllipsisIcon} from '@primer/octicons-react'
import type {Model} from '../../types'

export function TagsSection({tags}: {tags: string[]}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        gap: 2,
      }}
    >
      <Heading title="Tags" count={tags.length} />
      {tags.length > 0 && (
        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
          {tags.map(tag => (
            <Label key={tag} size="large" variant="secondary">
              {tag}
            </Label>
          ))}
        </Box>
      )}
    </Box>
  )
}

export function LanguagesSection({languages}: {languages: string[]}) {
  const [languagesIsOpen, setLanguagesIsOpen] = useState(false)
  const shouldTruncateLanguages = languages.length > 12
  let languagesString: string | undefined

  // Some languages are formatted with a comma e.g. `Spanish, Castilian` and we want to change them to `Spanish (Castilian)`
  const formattedLanguages = languages.map(language => {
    return language.includes(',') ? `${language.replace(/, /, ' (')})` : language
  })

  if (formattedLanguages.length === 1) {
    languagesString = formattedLanguages[0]
  } else if (shouldTruncateLanguages && !languagesIsOpen) {
    languagesString = formattedLanguages.slice(0, 12).join(', ')
  } else {
    const languagesButLast = formattedLanguages.slice(0, formattedLanguages.length - 1).join(', ')
    languagesString = `${languagesButLast}, and ${formattedLanguages[formattedLanguages.length - 1]}`
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Heading title="Languages" count={languages.length} />
      <Box>
        {languagesString}{' '}
        {shouldTruncateLanguages && !languagesIsOpen && (
          <>
            {' '}
            <Box
              as="button"
              aria-label="Reveal all languages"
              sx={{color: 'fg.muted', display: 'inline', cursor: 'pointer', border: 0, bg: 'transparent'}}
              onClick={() => setLanguagesIsOpen(true)}
            >
              <EllipsisIcon />
            </Box>
          </>
        )}
      </Box>
    </Box>
  )
}

const Heading = ({title, count}: {title: string; count?: number}) => (
  <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1}}>
    <Text as="h3" sx={{fontWeight: 'bold', fontSize: [1, 1, 2]}}>
      {title}
    </Text>
    {count && <CounterLabel>{count}</CounterLabel>}
  </Box>
)

const tokenLabel = (n: number) => `${Math.round(n / 1000)}k`
const capitalize = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}`

function InfoItem({isInline = false, children, label}: {isInline: boolean; children: React.ReactNode; label?: string}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        fontSize: 1,
        flex: 1,
        ...(isInline && {
          flexBasis: '140px',
          mr: [2, 2, 0],
        }),
      }}
    >
      {label && <Box sx={{color: 'fg.default', fontSize: 1, fontWeight: 'semibold', mb: 0}}>{label}</Box>}
      <Box sx={{color: 'fg.muted'}}>{children}</Box>
    </Box>
  )
}

export function ModelDetails({direction = 'column', model}: {direction?: 'column' | 'row'; model: Model}) {
  const {properties} = model
  const modelClass = properties.limits.rate?.model_class
  const maxOutputTokens = properties.limits.outputs.tokens ? tokenLabel(properties.limits.outputs.tokens) : ''
  const maxInputTokens = tokenLabel(properties.limits.inputs.tokens)
  const inline = direction === 'row'
  const layout = {
    column: {
      flexDirection: 'column',
    },
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
  }[direction]

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        gap: 2,
        ...layout,
      }}
    >
      <InfoItem isInline={inline} label="Context">
        {maxInputTokens} input {maxOutputTokens ? <>&middot; {maxOutputTokens} output</> : ``}
      </InfoItem>

      <InfoItem isInline={inline} label="Training date">
        {properties.training_data || 'Undisclosed'}
      </InfoItem>

      {modelClass && (
        <InfoItem isInline={inline} label="Rate limit tier">
          <Link inline={true} href="https://docs.github.com/en/github-models/prototyping-with-ai-models#rate-limits">
            {capitalize(modelClass)}
          </Link>
        </InfoItem>
      )}

      <InfoItem isInline={inline} label="Provider support">
        <Link inline={true} href={`https://ai.azure.com/github/support/${model.name}`}>
          Azure support site
        </Link>
      </InfoItem>
    </Box>
  )
}

export default function SidebarInfo({model}: {model: Model}) {
  const {task, properties, summary, tags} = model
  const labels = [...tags, task]
  const languages = properties.limits.languages.filter(language => language !== null)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Heading title="About" />
        <Text sx={{lineHeight: 1.5}}>{summary}</Text>
      </Box>
      <ModelDetails model={model} />
      {labels.length > 0 && <TagsSection tags={labels} />}
      {languages.length > 0 && <LanguagesSection languages={languages} />}
    </Box>
  )
}

try{ TagsSection.displayName ||= 'TagsSection' } catch {}
try{ LanguagesSection.displayName ||= 'LanguagesSection' } catch {}
try{ Heading.displayName ||= 'Heading' } catch {}
try{ InfoItem.displayName ||= 'InfoItem' } catch {}
try{ ModelDetails.displayName ||= 'ModelDetails' } catch {}
try{ SidebarInfo.displayName ||= 'SidebarInfo' } catch {}